import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Shared userPreferences favorites state (module store + optional React hook).
 * Not React Context — no Provider required.
 */
import { useSyncExternalStore } from 'react';
import { bindActionCreators } from 'redux';
import { evaluateSource } from '../../../behaviors/src/lib/evaluateSource';
import formActionCreators from '../../actions/formActionCreators';
import notificationActionCreators from '../../actions/notificationActionCreators';
import { configBehaviorsFunc } from '../../nativeBehaviors/configBehaviors';
import { nativeBehaviorEvaluator } from '../../nativeBehaviors/nativeBehaviorEvaluator';
import store from '../../reducers/storeRegistration';
import logger from '../../utils/logger';
import { buildFavoriteRemoveContentPathValue, buildFavoritesUpsertContentPathValue, FavoritesUnavailableError } from './favoritesHelpers';
export const USER_PREFERENCES_TYPE = 'userPreferences';
const formActions = bindActionCreators(formActionCreators, store.dispatch);
const notificationActions = bindActionCreators(notificationActionCreators, store.dispatch);
const notifyFavoritesError = function notifyFavoritesError(headerMessage, message) {
  let id = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'favorites-error';
  notificationActions.error({
    headerMessage,
    message,
    id
  });
};
const listeners = new Set();
let preferencesContent = {};
let loading = false;
let loadedForUserId = null;
let preferencesFormId = null;
let ensureInFlight = null;
let prevLoginUserId;

/** Cached snapshot; new object per getSnapshot breaks useSyncExternalStore. */
let cachedSnapshot = {
  preferencesContent,
  loading
};
const publish = () => {
  if (cachedSnapshot.preferencesContent === preferencesContent && cachedSnapshot.loading === loading) {
    return;
  }
  cachedSnapshot = {
    preferencesContent,
    loading
  };
  listeners.forEach(listener => listener());
};
const subscribe = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => cachedSnapshot;
const resetForUserChange = () => {
  loadedForUserId = null;
  preferencesFormId = null;
  ensureInFlight = null;
  preferencesContent = {};
  loading = false;
  publish();
};
store.subscribe(() => {
  var _store$getState$login;
  const userId = (_store$getState$login = store.getState().login.user) === null || _store$getState$login === void 0 ? void 0 : _store$getState$login.user_id;
  if (userId !== prevLoginUserId) {
    prevLoginUserId = userId;
    resetForUserChange();
  }
});
const loadUserPreferencesViaBehavior = async userId => {
  var _result$formId;
  const result = await evaluateSource({
    type: 'loadUserPreferencesFavoritesBehavior'
  }, {}, '/', {
    userId
  }, {
    evaluator: nativeBehaviorEvaluator.bind(null, {}, 'form'),
    configBehaviors: configBehaviorsFunc,
    errorHandler: () => {}
  });
  if (!result || typeof result !== 'object') {
    return {
      formId: null,
      content: {}
    };
  }
  return {
    formId: (_result$formId = result.formId) !== null && _result$formId !== void 0 ? _result$formId : null,
    content: result.content && typeof result.content === 'object' ? result.content : {}
  };
};
const snapshotFormActive = () => {
  var _f$active;
  const f = store.getState().form;
  return {
    id: ((_f$active = f.active) === null || _f$active === void 0 ? void 0 : _f$active.id) || null,
    pending: !!f.pendingChanges
  };
};
const restoreFormActive = async prev => {
  if (prev.id) {
    await formActions.serverShow(prev.id);
  } else {
    formActions.showCancel();
  }
};

/** Restore editor form after pin save; do not fail the pin if reload errors. */
const restoreFormActiveBestEffort = async prev => {
  var _store$getState$form$;
  if (!prev.id) {
    await restoreFormActive(prev);
    return true;
  }
  if (((_store$getState$form$ = store.getState().form.active) === null || _store$getState$form$ === void 0 ? void 0 : _store$getState$form$.id) === prev.id) {
    return true;
  }
  try {
    await restoreFormActive(prev);
    return true;
  } catch (e) {
    logger('userPreferencesFavorites: restore active form failed', e);
    notifyFavoritesError('Could not reload form', 'Your favorites were updated, but the open form could not be reloaded. Refresh if needed.', 'favorites-restore-failed');
    return false;
  }
};
const getLoginUserId = () => {
  var _store$getState$login2;
  return (_store$getState$login2 = store.getState().login.user) === null || _store$getState$login2 === void 0 ? void 0 : _store$getState$login2.user_id;
};
const isLoginUser = userId => getLoginUserId() === userId;
const applyLoadResult = (userId, formId, content) => {
  if (!isLoginUser(userId)) {
    return false;
  }
  preferencesFormId = formId;
  preferencesContent = content || {};
  loadedForUserId = userId;
  publish();
  return true;
};
const loadUserPreferences = async userId => {
  const _await$loadUserPrefer = await loadUserPreferencesViaBehavior(userId),
    formId = _await$loadUserPrefer.formId,
    content = _await$loadUserPrefer.content;
  if (!isLoginUser(userId)) {
    return null;
  }
  if (formId) {
    applyLoadResult(userId, formId, content);
    return formId;
  }
  preferencesFormId = null;
  preferencesContent = {};
  publish();
  return null;
};
export const ensureLoaded = async () => {
  const userId = getLoginUserId();
  if (!userId) return null;
  if (loadedForUserId === userId && preferencesFormId) {
    return preferencesFormId;
  }
  if (ensureInFlight) {
    return ensureInFlight;
  }
  const run = (async () => {
    loading = true;
    publish();
    try {
      return loadUserPreferences(userId);
    } finally {
      loading = false;
      ensureInFlight = null;
      publish();
    }
  })();
  ensureInFlight = run;
  return run;
};
const mutateFavoritesAndSave = async resolvePathAndValue => {
  const formId = preferencesFormId;
  if (!formId) return false;
  const snap = snapshotFormActive();
  if (snap.pending && snap.id && snap.id !== formId) {
    notificationActions.info({
      headerMessage: 'Cannot update favorites',
      message: 'Please save or discard changes on the open form first.',
      id: 'favorites-blocked-pending'
    });
    return false;
  }
  const needSwap = snap.id !== formId;
  const prevSnap = _objectSpread({}, snap);
  try {
    var _store$getState$form$2, _store$getState$form$3, _store$getState$form$4;
    if (needSwap) {
      await formActions.serverShow(formId);
    }
    const content = ((_store$getState$form$2 = store.getState().form.active) === null || _store$getState$form$2 === void 0 ? void 0 : _store$getState$form$2.content) || {};
    const _resolvePathAndValue = resolvePathAndValue(content),
      contentPath = _resolvePathAndValue.contentPath,
      value = _resolvePathAndValue.value;
    if (!contentPath) {
      return false;
    }
    await formActions.change(formId, contentPath, value);
    const version = (_store$getState$form$3 = store.getState().form.active) === null || _store$getState$form$3 === void 0 || (_store$getState$form$3 = _store$getState$form$3.content) === null || _store$getState$form$3 === void 0 ? void 0 : _store$getState$form$3.version;
    await formActions.serverSave(version);
    preferencesContent = ((_store$getState$form$4 = store.getState().form.active) === null || _store$getState$form$4 === void 0 ? void 0 : _store$getState$form$4.content) || {};
    publish();
    return true;
  } finally {
    if (needSwap) {
      await restoreFormActiveBestEffort(prevSnap);
    }
  }
};
export const upsertPinnedFavorite = async favorite => {
  await ensureLoaded();
  if (!preferencesFormId) {
    throw new FavoritesUnavailableError();
  }
  return mutateFavoritesAndSave(c => buildFavoritesUpsertContentPathValue(c, favorite));
};
export const removePinnedFavorite = async pinId => {
  await ensureLoaded();
  if (!preferencesFormId) {
    throw new FavoritesUnavailableError();
  }
  return mutateFavoritesAndSave(c => buildFavoriteRemoveContentPathValue(c, pinId));
};
export const getPreferencesContent = () => preferencesContent;

/** Subscribe to module store so UI updates after pin/unpin (not React Context). */
export function useUserPreferencesFavorites() {
  const _useSyncExternalStore = useSyncExternalStore(subscribe, getSnapshot, getSnapshot),
    content = _useSyncExternalStore.preferencesContent,
    isLoading = _useSyncExternalStore.loading;
  return {
    preferencesContent: content,
    loading: isLoading,
    ensureLoaded,
    getPreferencesContent,
    upsertPinnedFavorite,
    removePinnedFavorite
  };
}